import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import FuseLoading from '@fuse/core/FuseLoading';
import SimpleDialog from '@fuse/core/SimpleDialog/SimpleDialog';
import handleError from '@fuse/core/errorHandler';
import UsersTableHead from './UsersTableHead';
import AddUserStatusForm from './AddUserStatusForm';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Button, Pagination } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import LinkHRMForm from './components/LinkHRMForm';
import { getAbsoluteFileUrl, normalizeFIO } from 'src/app/helpers/utils';
import { debounce } from 'lodash';
import NoDataComponent from 'app/shared-components/noDataComponent/NoDataComponent';
import { useSearchParams } from 'react-router-dom';
import { useFetchUserListMutation } from 'src/app/services/accountApi';
import PaginationChangeRange from 'app/shared-components/PaginationChangeRange/PaginationChangeRange';
import ConfirmDeleteModalWindow from 'app/shared-components/confirmDeleteModalWindow';
import { useUnbindUserMutation } from 'src/app/services/adApi';
import useMessage from 'src/app/hooks/useMessage';

export const userStatusState = [
  {
    id: 0,
    statusName: 'None',
    title: 'Нет статуса',
  },
  {
    id: 1,
    statusName: 'Top',
    title: 'Топ',
  },
  {
    id: 2,
    statusName: 'Volonter',
    title: 'Волонтер',
  },
  {
    id: 3,
    statusName: 'Stajer',
    title: 'Стажер',
  },
  {
    id: 4,
    statusName: 'Probation',
    title: 'Испытательный срок',
  },
];

function UsersTable() {
  const { t } = useTranslation('translations');
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const initialPage = parseInt(searchParams.get('page')) || 1;
  const [page, setPage] = useState(initialPage);
  const [data, setData] = useState(null);
  const [limit, setLimit] = useState(20);
  const [searchUser, setSearchUser] = useState(initialSearch);
  const [totalPages, setTotalPages] = useState(0);
  const [isOpenedModal, setIsOpenedModal] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const { showMessage } = useMessage();

  const [unbindEmployeeFromAdAccount] = useUnbindUserMutation();

  const initialState = {
    filters: '{}',
    first: 0,
    rows: limit,
    sortOrder: 1,
    sortField: 'id',
    globalFilter: null,
  };

  const [pagination, setPagination] = useState({
    ...initialState,
    first: (initialPage - 1) * initialState.rows,
  });
  const [getUsersList, { isLoading }] = useFetchUserListMutation();

  const getUsers = async (params) => {
    try {
      const response = await getUsersList(params);
      setData(response?.data);
      setTotalPages(Math.ceil(response?.data?.totalItems / pagination.rows));
    } catch (e) {
      handleError(e);
    }
  };
  const debounceDelay = 650;
  const debouncedGetUsers = useCallback(
    debounce((searchText) => {
      getUsers({
        ...pagination,
        first: (page - 1) * limit,
        filters: searchText ? JSON.stringify({ fullName: { value: searchText } }) : '{}',
      });
    }, debounceDelay),
    [pagination, page, limit] // These dependencies are now properly accounted for
  );

  useEffect(() => {
    debouncedGetUsers(searchUser);
  }, [pagination, searchUser, limit]);
  useEffect(() => {
    return () => {
      debouncedGetUsers.cancel();
    };
  }, [debouncedGetUsers]);
  const handleSearchUser = (e) => {
    const searchText = e.target.value.trim();
    setSearchUser(searchText);
    const params = new URLSearchParams(searchParams);
    if (searchText) {
      params.set('search', searchText);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    setSearchParams(params);
    setPage(1);
    debouncedGetUsers(searchText);
    setPagination((prev) => ({ ...prev, first: 0 }));
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
    setPagination((prev) => ({
      ...prev,
      rows: newLimit,
      first: 0,
    }));
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    setPagination((prev) => ({ ...prev, first: (value - 1) * prev.rows }));
    const params = new URLSearchParams(searchParams);
    params.set('page', value);
    setSearchParams(params);
  };

  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page')) || 1;
    const urlSearch = searchParams.get('search') || '';
    if (urlPage !== page) setPage(urlPage);
    if (urlSearch !== searchUser) setSearchUser(urlSearch);
  }, [searchParams]);

  const [open, setOpen] = useState(false);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState({ roles: [] });
  const [resizeViewport, setReziseViewport] = useState(window.innerWidth);

  useEffect(() => {
    const resizeHandler = () => setReziseViewport(window.innerWidth);
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);
  const handleClickSettings = (user) => () => {
    setSelectedUser(user);
    setOpen(true);
  };
  const handleClose = async () => {
    getUsers({
      ...pagination,
      filters: searchUser ? JSON.stringify({ fullName: { value: searchUser } }) : '{}',
    });
    setOpen(false);
  };

  const handleShowModal = (id) => {
    setIsOpenedModal(true);
    setEmployeeId(id);
  };

  const handleCloseModal = () => {
    setIsOpenedModal(false);
  };

  const unbindEmployeeFromUser = (userId) => {
    unbindEmployeeFromAdAccount({ userId })
      .unwrap()
      .then((res) => {
        if (res.data === userId) {
          showMessage({ message: t('AdAccountSuccessfullyUnbinded'), variant: 'success' });
          getUsers({
            ...pagination,
            filters: searchUser ? JSON.stringify({ fullName: { value: searchUser } }) : '{}',
          });
        }
      })
      .catch((error) => {
        showMessage({ message: t('ErrorOccuredOpenBrowserConsole'), variant: 'error' });
        console.error(error);
      });
  };

  return (
    <div className="flex flex-col w-full">
      <SimpleDialog open={open} onClose={() => setOpen(false)} title={selectedUser.userName}>
        <LinkHRMForm selectedUser={selectedUser} onCloseAndRefresh={handleClose} />
      </SimpleDialog>
      <SimpleDialog
        open={openStatusModal}
        onClose={() => setOpenStatusModal(false)}
        title={t('SelectUserStatus')}
      >
        <AddUserStatusForm
          selectedUser={selectedUser}
          onClose={() => setOpenStatusModal(false)}
          userStatusState={userStatusState}
        />
      </SimpleDialog>
      <div>
        <Table
          stickyheaderaria-labelledby="tableTitle"
          className={`${resizeViewport < 1575 ? 'flex flex-col items-center' : ''}`}
        >
          <UsersTableHead
            rowCount={data?.length}
            handleSearchUser={handleSearchUser}
            searchUser={searchUser}
          />
          {isLoading && (
            <TableBody>
              <TableRow>
                <TableCell colSpan={3} align="center" className="h-[70vh]">
                  <FuseLoading />
                </TableCell>
              </TableRow>
            </TableBody>
          )}
          {!isLoading && data?.items?.length > 0 && (
            <TableBody>
              {data.items?.map((user) => {
                return (
                  <TableRow
                    className="flex flex-col items-center bg-white dark:bg-dark md:border-none md:table-row"
                    hover
                    tabIndex={-1}
                    key={user.id}
                  >
                    <TableCell className="p-4 md:p-16" component="th" scope="row">
                      <Box display="flex" alignItems="center">
                        <Avatar
                          src={getAbsoluteFileUrl(user.staffImageFileUrl)}
                          className="w-40 h-40"
                        />
                        <Typography className="ml-8">{user.userName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell className="p-4 text-center md:p-16" component="th" scope="row">
                      {user.name ? normalizeFIO(user.name) : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {!user.staffId ? (
                        <Button variant="outlined" color="info" onClick={handleClickSettings(user)}>
                          {t('ConnectWithHRM')}
                        </Button>
                      ) : user.name ? (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleShowModal(user.id)}
                        >
                          {t('DisconnectFromHRM')}
                        </Button>
                      ) : (
                        <span>{t('NoActionsAvailable')}</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      </div>

      {data?.items && data.items?.length === 0 && !isLoading && (
        <div className="flex items-center justify-center h-[70vh]">
          <NoDataComponent />
        </div>
      )}
      {totalPages > 1 && !isLoading && (
        <div className="flex items-center justify-between px-5">
          <PaginationChangeRange value={limit} setValue={handleLimitChange} />
          <Pagination
            className="mx-auto mb-8"
            count={totalPages}
            page={page}
            onChange={handlePageChange}
          />
          <div className="w-[100px]" />
        </div>
      )}
      {isOpenedModal && (
        <ConfirmDeleteModalWindow
          onClose={handleCloseModal}
          setIsOpenModal={setIsOpenedModal}
          modalTitle={'AreYouSureToUnbindEmployeeFromAdAccount'}
          deleteMessage={() => unbindEmployeeFromUser(employeeId)}
          open={isOpenedModal}
        />
      )}
    </div>
  );
}
export default UsersTable;
